/**
 * PD-17595 — what each account (client lifecycle) stage MEANS: what the
 * company is to us, not a step of a sale (Chris, 10/26/26). The sales process
 * lives in campaigns (per person) and opportunities (per deal).
 *
 * Tenants name their stages as they like and may have several stages with
 * one meaning (Pharmsource's Cold, Warm and Hot are all Lead). Code, the
 * default Clients tabs and the automatic changes read the meaning, so a
 * rename never empties a tab.
 */
export const ACCOUNT_STAGE_MEANINGS = [
	'lead',
	'customer',
	'dormant',
	'not_interested',
	'closed',
	'disqualified',
	'partner',
	'other',
] as const;
export type AccountStageMeaning = (typeof ACCOUNT_STAGE_MEANINGS)[number];

export const ACCOUNT_STAGE_MEANING_LABELS: Readonly<Record<AccountStageMeaning, string>> = {
	lead: 'Lead',
	customer: 'Customer',
	dormant: 'Dormant',
	not_interested: 'Not Interested',
	closed: 'Closed',
	disqualified: 'Disqualified',
	partner: 'Partner',
	other: 'Other',
};

/**
 * A new tenant's stages (title case). There is no New stage: the New Leads
 * tab is Leads created in the last N days (`NEW_LEAD_DAYS_SETTING`).
 */
export const DEFAULT_ACCOUNT_STAGES: ReadonlyArray<{
	name: string;
	meaning: AccountStageMeaning;
	isClosedType: boolean;
}> = [
	{ name: 'Lead', meaning: 'lead', isClosedType: false },
	{ name: 'Customer', meaning: 'customer', isClosedType: false },
	{ name: 'Dormant', meaning: 'dormant', isClosedType: false },
	// Closed type as in the earlier seed (PopulateClientLifecycleStages).
	{ name: 'Not Interested', meaning: 'not_interested', isClosedType: true },
	{ name: 'Closed', meaning: 'closed', isClosedType: true },
	{ name: 'Disqualified', meaning: 'disqualified', isClosedType: false },
	{ name: 'Partner', meaning: 'partner', isClosedType: false },
];

/**
 * Meanings an automatic change never moves an account out of: a campaign
 * reply or a won deal does not override a customer, partner, disqualified or
 * closed account.
 */
export const PROTECTED_ACCOUNT_STAGE_MEANINGS: ReadonlyArray<AccountStageMeaning> = [
	'customer',
	'partner',
	'disqualified',
	'closed',
];

/** Instance setting: how many days a Lead counts as new on the New Leads tab. */
export const NEW_LEAD_DAYS_SETTING = 'clients.new_lead_days';
export const NEW_LEAD_DAYS_DEFAULT = 30;

/**
 * The meaning a stage has from its name alone, for stages that existed before
 * meanings (matched ignoring case and spaces). The old sales-step stages
 * (New, Qualification, Proposal, Negotiation) are leads; an unknown name is
 * 'other' and an admin picks its meaning in the stage editor.
 */
export function accountStageMeaningForName(name: string | null | undefined): AccountStageMeaning {
	switch ((name ?? '').trim().toLowerCase()) {
		case 'lead':
		case 'leads':
		case 'new':
		case 'qualification':
		case 'proposal':
		case 'negotiation':
		case 'prospecting':
		case 'prospect':
		case 'cold':
		case 'warm':
		case 'hot':
			return 'lead';
		case 'customer':
		case 'customers':
		case 'active':
			return 'customer';
		case 'dormant':
			return 'dormant';
		case 'not interested':
			return 'not_interested';
		case 'closed':
			return 'closed';
		case 'disqualified':
		case 'non-viable':
			return 'disqualified';
		case 'partner':
			return 'partner';
		default:
			return 'other';
	}
}

export function isAccountStageMeaning(value: unknown): value is AccountStageMeaning {
	return typeof value === 'string' && (ACCOUNT_STAGE_MEANINGS as ReadonlyArray<string>).includes(value);
}
